import { FC, Component, ReactNode, ContextType } from "react";
import { Route, Switch } from "react-router-dom";
import moment from "moment";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { GAContext } from "@ctra/analytics";
import { Breadcrumbs, Enterprise } from "@ctra/api";

import { Routes } from "@routes";
import { FarmContext, useFarm } from "@farms";

import { GACategories, SectionVariant, CompareChartsPage } from "@analytics";

import { MetricSelectorPage } from "..";

import styles from "./AnalyticsPage.module.less";
import { DashboardPage } from "../DashboardPage";
import { AddChartPage } from "../AddChartPage";

const {
  components: { Breadcrumb }
} = Breadcrumbs;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary to catch errors in child components
 */
class ErrorBoundary extends Component<Props, State> {
  static contextType = FarmContext.Context;
  context!: ContextType<typeof FarmContext.Context>;

  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false
    };
  }

  static getDerivedStateFromError(): State {
    return {
      hasError: true
    };
  }

  componentDidCatch() {
    /**
     * Check if the farm context was updated in the last 3 seconds
     * @type {boolean | undefined}
     */
    const farmSwitchRelated = this.context.meta.lastContextUpdate.isAfter(moment().subtract(3, "seconds"));

    if (farmSwitchRelated) {
      // Redirect to analytics overview page if the error is related to a farm switch
      Enterprise.history.push(Routes.app.analytics.index);
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

/**
 * Analytics route page
 * @constructor
 */
const AnalyticsPage: FC = () => {
  const { t } = useTranslation();
  const { farm } = useFarm();

  const {
    navigation: {
      main: { analytics }
    }
  } = Content;

  return (
    <>
      <Breadcrumb
        path={Routes.app.analytics.index}
        title={t<string>(analytics, {
          variant: farm ? SectionVariant.farm : SectionVariant.overview
        })}
      />
      <section className={styles.PageContent}>
        <Switch>
          <Route
            exact
            path={[Routes.app.analytics.dashboard.addChart.index, Routes.app.analytics.compareCharts.index]}
          >
            <GAContext.Provider value={{ category: GACategories.configurator }}>
              <ErrorBoundary>
                <MetricSelectorPage />
              </ErrorBoundary>
            </GAContext.Provider>
          </Route>
          <Route exact path={Routes.app.analytics.dashboard.addChart.metric}>
            <GAContext.Provider value={{ category: GACategories.configurator }}>
              <ErrorBoundary>
                <AddChartPage />
              </ErrorBoundary>
            </GAContext.Provider>
          </Route>
          <Route
            exact
            path={[
              Routes.app.analytics.compareCharts.withMetric,
              Routes.app.analytics.compareCharts.withCard
            ]}
          >
            <GAContext.Provider value={{ category: GACategories.compareCharts }}>
              <ErrorBoundary>
                <CompareChartsPage />
              </ErrorBoundary>
            </GAContext.Provider>
          </Route>
          <Route path={[Routes.app.analytics.dashboard.index, Routes.app.analytics.index]}>
            <GAContext.Provider value={{ category: GACategories.dashboards }}>
              <DashboardPage />
            </GAContext.Provider>
          </Route>
        </Switch>
      </section>
    </>
  );
};

export default AnalyticsPage;
